import { useEffect, useState } from 'react';
import { supabase } from '../../supabase';

// Profile photo review (Apple Guideline 1.2 — UGC moderation).
// New and changed profile photos are hidden from other users until approved
// here. Mirrors the Selfies review workflow.
export default function Photos() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');
  const [filter, setFilter] = useState('pending'); // pending | approved | rejected | all

  const load = async () => {
    setLoading(true);
    let q = supabase
      .from('profiles')
      .select('id, name, age, phone, photos, photos_status, photos_reviewed_at, photos_review_note, created_at')
      .not('photos', 'is', null)
      .order('created_at', { ascending: false });

    if (filter !== 'all') q = q.eq('photos_status', filter);

    const { data, error } = await q;
    if (error) console.error(error);
    setProfiles((data || []).filter(p => Array.isArray(p.photos) && p.photos.length > 0));
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const adminPhone = sessionStorage.getItem('admin_phone');

  const review = async (status) => {
    if (!selected) return;
    await supabase.from('profiles').update({
      photos_status: status,
      photos_reviewed_at: new Date().toISOString(),
      photos_review_note: note || null,
      photos_reviewed_by: adminPhone,
    }).eq('id', selected.id);
    setSelected(null);
    setNote('');
    load();
  };

  const badge = (s) => s === 'approved' ? '✓ Approved' : s === 'rejected' ? '✗ Rejected' : '⏳ Pending';

  return (
    <div>
      <div style={S.headerRow}>
        <h1 style={S.h1}>Photo review</h1>
        <div style={S.filters}>
          {['pending', 'approved', 'rejected', 'all'].map(f => (
            <button key={f}
              onClick={() => setFilter(f)}
              style={{...S.filterBtn, ...(filter === f ? S.filterBtnActive : {})}}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {loading ? <p style={S.dim}>Loading…</p> :
        profiles.length === 0 ? <p style={S.dim}>No profiles in this view.</p> :
        <div style={S.grid}>
          {profiles.map(p => (
            <div key={p.id} style={S.card} onClick={() => { setSelected(p); setNote(p.photos_review_note || ''); }}>
              <img src={p.photos[0]} alt="profile" style={S.thumb} />
              <div style={S.cardBody}>
                <div style={S.name}>{p.name || '(no name)'} <span style={S.age}>{p.age ? `, ${p.age}` : ''}</span></div>
                <div style={S.meta}>{p.phone} · {p.photos.length} photo{p.photos.length === 1 ? '' : 's'}</div>
                <div style={S.badge}>{badge(p.photos_status)}</div>
              </div>
            </div>
          ))}
        </div>
      }

      {selected && (
        <div style={S.modalBg} onClick={() => setSelected(null)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={S.photoGrid}>
              {selected.photos.map((url, i) => (
                <img key={i} src={url} alt={`upload ${i + 1}`} style={S.gridImg} />
              ))}
            </div>
            <div style={S.modalBody}>
              <h2 style={S.h2}>{selected.name || '(no name)'} {selected.age ? `, ${selected.age}` : ''}</h2>
              <p style={S.dim}>{selected.phone}</p>
              <p style={S.dim}>Joined {new Date(selected.created_at).toLocaleDateString()}</p>
              {selected.photos_reviewed_at && <p style={S.dim}>Previously reviewed {new Date(selected.photos_reviewed_at).toLocaleString()}</p>}
              <textarea
                style={S.textarea}
                placeholder="Note (optional) — e.g. 'photo 2 inappropriate', 'all clear'"
                value={note}
                onChange={e => setNote(e.target.value)}
              />
              <div style={S.actions}>
                <button style={{...S.btn, ...S.btnReject}} onClick={() => review('rejected')}>✗ Reject</button>
                <button style={{...S.btn, ...S.btnApprove}} onClick={() => review('approved')}>✓ Approve</button>
              </div>
              <button style={S.cancel} onClick={() => setSelected(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  h1: { fontSize: 28, fontWeight: 800, margin: 0 },
  h2: { fontSize: 22, fontWeight: 800, margin: '0 0 4px 0' },
  dim: { color: '#888', fontSize: 13, margin: '4px 0' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  filters: { display: 'flex', gap: 8 },
  filterBtn: { padding: '6px 14px', borderRadius: 999, background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#888', cursor: 'pointer', fontSize: 13 },
  filterBtnActive: { background: 'rgba(167,139,250,0.15)', color: '#a78bfa' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 },
  card: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, overflow: 'hidden', cursor: 'pointer' },
  thumb: { width: '100%', height: 200, objectFit: 'cover', display: 'block', background: '#111' },
  cardBody: { padding: 12 },
  name: { fontSize: 15, fontWeight: 700 },
  age: { color: '#888', fontWeight: 400 },
  meta: { color: '#888', fontSize: 12, marginTop: 4 },
  badge: { marginTop: 8, fontSize: 11, fontWeight: 700, color: '#a78bfa' },
  modalBg: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 },
  modal: { background: '#1a0a2e', borderRadius: 16, maxWidth: 640, width: '100%', maxHeight: '90vh', overflow: 'auto' },
  photoGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, background: '#000', padding: 4 },
  gridImg: { width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block', borderRadius: 4 },
  modalBody: { padding: 24 },
  textarea: { width: '100%', boxSizing: 'border-box', minHeight: 80, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff', fontSize: 14, padding: 12, marginTop: 16, fontFamily: 'inherit', outline: 'none', resize: 'vertical' },
  actions: { display: 'flex', gap: 8, marginTop: 16 },
  btn: { flex: 1, padding: 14, borderRadius: 999, fontSize: 14, fontWeight: 700, border: 'none', cursor: 'pointer' },
  btnApprove: { background: '#34d399', color: '#000' },
  btnReject: { background: 'rgba(248,113,113,0.15)', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' },
  cancel: { width: '100%', padding: 10, marginTop: 8, background: 'none', border: 'none', color: '#888', fontSize: 13, cursor: 'pointer' },
};
